import * as React from 'react';
import { alpha, useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import moment from 'moment';
import * as Device from 'react-device-detect';

import { useStatusBarScrollToTop } from '@hooks';
import { BookingStatusEnum, GetSpotBookingQuery } from '@graphql';
import { getStatusColor } from '@utils';

interface BookingSpotProps {
  booking: GetSpotBookingQuery['booking'];
  handleEdit: () => void;
}

export default function BookingSpot(props: Readonly<BookingSpotProps>) {
  const { booking, handleEdit } = props;
  const { t } = useTranslation('booking');
  const theme = useTheme();

  // Handle iOS status bar tap to scroll to top
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  useStatusBarScrollToTop(scrollRef);

  const lastConsumerUpdateStatus = booking.updates
    .filter((update) => update.status && update.author.__typename === 'Consumer')
    .pop();

  return (
    <Stack
      ref={scrollRef}
      width="100%"
      height="100%"
      spacing={5}
      justifyContent="flex-start"
      py={Device.isMobileOnly ? 5 : undefined}
      px={2}
      alignItems="center"
      overflow="auto"
    >
      {lastConsumerUpdateStatus?.status !== BookingStatusEnum.Canceled && (
        <Stack
          position="absolute"
          width="100%"
          bottom={0}
          left={0}
          right={0}
          pt={2}
          pb={`calc(env(safe-area-inset-bottom) + 16px)`}
          px={2}
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(10px)',
            borderTop: `1px solid ${alpha('#ffffff', 0.1)} `,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
          }}
        >
          <Button
            fullWidth
            color="uncolored"
            variant={moment(booking.startTime).isBefore() ? 'contained' : 'outlined'}
            disabled={moment(booking.startTime).isBefore()}
            startIcon={<theme.icons.edit />}
            onClick={handleEdit}
          >
            {t('update', { ns: 'cta' })}
          </Button>
        </Stack>
      )}
      {lastConsumerUpdateStatus?.status === BookingStatusEnum.Canceled && (
        <Alert severity="warning">{t('canceledByConsumer')}</Alert>
      )}
      <Stack pb={`calc(env(safe-area-inset-bottom) + 50px)`} spacing={2} width="100%" divider={<Divider />}>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Typography variant="body2">Ref</Typography>
          <Typography variant="body2">{booking.ref}</Typography>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Creator')}</Typography>
          <Typography variant="body2">{t(`origin.${booking.origin}`)}</Typography>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Client')}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar src={getClientAvatar(booking)} sx={{ width: 30, height: 30 }} />
            <Typography variant="body2">{getClientName(booking, t)}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Date')}</Typography>
          <Typography variant="body2">{moment(booking.startTime).format('dddd LL')}</Typography>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Hour')}</Typography>
          <Typography variant="body2">{getHour(booking)}</Typography>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Status')}</Typography>
          <Chip
            label={
              booking.status === BookingStatusEnum.Pending && moment(booking.startTime).isBefore(new Date())
                ? t('status.EXPIRED', { ns: 'booking' })
                : t(`status.${booking.status}`, { ns: 'booking' })
            }
            color={getStatusColor(booking.status)}
          />
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Participants')}</Typography>
          <Typography variant="body2">{booking.participants}</Typography>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Phone')}</Typography>
          <Typography variant="body2">{getClientPhone(booking, t)}</Typography>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Age')}</Typography>
          <Typography variant="body2">{getClientAge(booking, t)}</Typography>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Gender')}</Typography>
          <Typography variant="body2">{getClientGender(booking, t)}</Typography>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">{t('dataGrid.Occasion')}</Typography>
          <Typography variant="body2">
            {booking.occasion ? t(`occasion.${booking.occasion}`) : t(`word.null`, { ns: 'common' })}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

function getHour(booking: GetSpotBookingQuery['booking']) {
  const nearbyTime = moment(booking.startTime).fromNow();
  let date = moment(booking.startTime).format('HH:mm ');

  if (moment().isSame(booking.startTime, 'day')) {
    date += ` (${nearbyTime})`;
  }

  return date;
}

function getClientName(booking: GetSpotBookingQuery['booking'], t: TFunction) {
  let clientName = '';

  if (booking.client.__typename === 'BookingClient') {
    clientName = booking.client.firstname;

    if (booking.client.lastname) {
      clientName += ` ${booking.client.lastname}`;
    }
  } else if (booking.client.__typename === 'Consumer') {
    if (booking.client.user.deletedAt) {
      return t(`user.deleted user`, { ns: 'account' });
    }
    clientName = booking.client.user.firstname + ' ' + booking.client.user.lastname;
  }

  return clientName;
}

function getClientAvatar(booking: GetSpotBookingQuery['booking']) {
  let avatar: string | null = null;

  if (booking.client.__typename === 'Consumer' && booking.client.avatar?.url) {
    avatar = booking.client.avatar.url;
  }

  return avatar;
}

function getClientAge(booking: GetSpotBookingQuery['booking'], t: TFunction) {
  return booking.client.__typename === 'Consumer' && booking.client.user.birthdate
    ? moment(booking.client.user.birthdate).fromNow(true)
    : t(`word.null`, { ns: 'common' });
}

function getClientPhone(booking: GetSpotBookingQuery['booking'], t: TFunction) {
  let phone = '';

  if (booking.client.__typename === 'BookingClient') {
    phone = booking.client.clientPhone;
  } else if (booking.client.__typename === 'Consumer') {
    if (booking.client.user.deletedAt) {
      return t('word.null', { ns: 'common' });
    }
    phone = booking.client.user.mobilePhone ?? '';
  }

  return phone.replace(/(\d{2})(?=\d)/g, '$1 ');
}

function getClientGender(booking: GetSpotBookingQuery['booking'], t: TFunction) {
  return booking.client.__typename === 'Consumer'
    ? t(`gender.${booking.client?.user.gender}`, { ns: 'common' })
    : t(`word.null`, { ns: 'common' });
}
